import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Paper, Table,
    TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography, Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { AuthService } from '../../services/auth.service';
import { ExchangeRateService } from '../../services/exchangeRate.service';
import { ExchangeRate, ExchangeRateImportResult, ExchangeRateSource } from '../../interfaces/fx.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';

const SOURCES: ExchangeRateSource[] = ['MANUAL', 'BANK', 'IMPORT'];

interface Draft { id?: string; currencyCode: string; rateDate: string; rate: string; source: ExchangeRateSource; remarks: string }

const emptyDraft = (): Draft => ({ currencyCode: '', rateDate: toLocalIsoDate(), rate: '', source: 'MANUAL', remarks: '' });

/**
 * Per-institution exchange-rate table: BDT per one unit of a currency on a date. The rate used for a
 * date is the latest one on or before it, so month-end revaluation only needs a rate entered at or
 * before month end. Paste-CSV import takes `date,currency,rate` lines.
 */
const ExchangeRates: React.FC = () => {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.ACCOUNT_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;

    const [rows, setRows] = useState<ExchangeRate[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [currency, setCurrency] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [draft, setDraft] = useState<Draft | null>(null);
    const [importOpen, setImportOpen] = useState(false);
    const [csv, setCsv] = useState('');
    const [importResult, setImportResult] = useState<ExchangeRateImportResult | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await ExchangeRateService.list(institutionId, {
                currency: currency.trim() || undefined, from: from || undefined, to: to || undefined, page, size,
            });
            setRows(res.content ?? []);
            setTotal(res.totalElements ?? 0);
        } catch (e) {
            toast.showApiError(e, 'Failed to load exchange rates');
        }
    }, [institutionId, currency, from, to, page, size]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const draftValid = !!draft && /^[A-Z]{3}$/.test(draft.currencyCode) && draft.currencyCode !== 'BDT'
        && !!draft.rateDate && Number(draft.rate) > 0;

    const save = async () => {
        if (!draft || !draftValid) return;
        setBusy(true);
        try {
            const req = {
                currencyCode: draft.currencyCode, rateDate: draft.rateDate, rate: Number(draft.rate),
                source: draft.source, remarks: draft.remarks.trim() || null,
            };
            if (draft.id) await ExchangeRateService.update(institutionId, draft.id, req);
            else await ExchangeRateService.create(institutionId, req);
            toast.showSuccess('Exchange rate saved');
            setDraft(null);
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to save exchange rate');
        } finally {
            setBusy(false);
        }
    };

    const remove = async (r: ExchangeRate) => {
        if (!window.confirm(`Delete the ${r.currencyCode} rate for ${formatDate(r.rateDate)}?`)) return;
        try {
            await ExchangeRateService.remove(institutionId, r.id);
            toast.showSuccess('Exchange rate deleted');
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to delete exchange rate');
        }
    };

    const runImport = async () => {
        setBusy(true);
        try {
            const res = await ExchangeRateService.importCsv(institutionId, csv);
            setImportResult(res);
            if (res.created + res.updated > 0) load();
        } catch (e) {
            toast.showApiError(e, 'Failed to import rates');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box data-help-page="accounting.exchangeRates.page" sx={{ p: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h5">Exchange rates</Typography>
            <Typography variant="body2" color="text.secondary">
                BDT per one unit of the foreign currency. The rate in force on a date is the latest one on or before it.
            </Typography>

            <Box data-help-section="accounting.exchangeRates.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <TextField data-help="accounting.exchangeRates.currency" label="Currency" size="small" value={currency}
                    onChange={e => { setCurrency(e.target.value.toUpperCase()); setPage(0); }}
                    inputProps={{ maxLength: 3 }} sx={{ flex: '1 1 140px', maxWidth: 180 }} />
                <TextField data-help="accounting.exchangeRates.from" label="From" type="date" size="small" value={from} InputLabelProps={{ shrink: true }}
                    onChange={e => { setFrom(e.target.value); setPage(0); }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                <TextField data-help="accounting.exchangeRates.to" label="To" type="date" size="small" value={to} InputLabelProps={{ shrink: true }}
                    onChange={e => { setTo(e.target.value); setPage(0); }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                {canManage && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button data-help="accounting.exchangeRates.add" variant="contained" onClick={() => setDraft(emptyDraft())}>Add rate</Button>
                        <Button data-help="accounting.exchangeRates.import" variant="outlined" onClick={() => { setCsv(''); setImportResult(null); setImportOpen(true); }}>
                            Import CSV
                        </Button>
                    </Box>
                )}
            </Box>

            <Paper>
                <TableContainer data-help-section="accounting.exchangeRates.table">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="accounting.exchangeRates.col.date">Date</TableCell>
                                <TableCell data-help="accounting.exchangeRates.col.currency">Currency</TableCell>
                                <TableCell data-help="accounting.exchangeRates.col.rate" align="right">Rate (BDT)</TableCell>
                                <TableCell data-help="accounting.exchangeRates.col.source">Source</TableCell>
                                <TableCell data-help="accounting.exchangeRates.col.remarks">Remarks</TableCell>
                                {canManage && <TableCell data-help="accounting.exchangeRates.col.actions" align="right">Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(r => (
                                <TableRow key={r.id} hover>
                                    <TableCell>{formatDate(r.rateDate)}</TableCell>
                                    <TableCell>{r.currencyCode}</TableCell>
                                    <TableCell align="right">{Number(r.rate).toFixed(4)}</TableCell>
                                    <TableCell>{r.source}</TableCell>
                                    <TableCell>{r.remarks ?? ''}</TableCell>
                                    {canManage && (
                                        <TableCell align="right">
                                            <IconButton data-help="accounting.exchangeRates.action.edit" size="small" aria-label={`Edit ${r.currencyCode} ${r.rateDate}`}
                                                onClick={() => setDraft({
                                                    id: r.id, currencyCode: r.currencyCode, rateDate: r.rateDate,
                                                    rate: String(r.rate), source: r.source, remarks: r.remarks ?? '',
                                                })}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton data-help="accounting.exchangeRates.action.delete" size="small" aria-label={`Delete ${r.currencyCode} ${r.rateDate}`}
                                                onClick={() => remove(r)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={canManage ? 6 : 5} align="center">No exchange rates yet.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination data-help="accounting.exchangeRates.paging" component="div" count={total} page={page} rowsPerPage={size}
                    rowsPerPageOptions={[25, 50, 100]}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }} />
            </Paper>

            <Dialog data-help-page="accounting.exchangeRates.form.page" open={!!draft} onClose={() => setDraft(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{draft?.id ? 'Edit exchange rate' : 'Add exchange rate'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                    <TextField data-help="accounting.exchangeRates.form.field.currency" label="Currency" required fullWidth value={draft?.currencyCode ?? ''}
                        disabled={!!draft?.id} inputProps={{ maxLength: 3 }}
                        error={!!draft?.currencyCode && !/^[A-Z]{3}$/.test(draft.currencyCode)}
                        onChange={e => draft && setDraft({ ...draft, currencyCode: e.target.value.toUpperCase() })} />
                    <TextField data-help="accounting.exchangeRates.form.field.date" label="Rate date" type="date" required fullWidth value={draft?.rateDate ?? ''}
                        disabled={!!draft?.id} InputLabelProps={{ shrink: true }}
                        onChange={e => draft && setDraft({ ...draft, rateDate: e.target.value })} />
                    <TextField data-help="accounting.exchangeRates.form.field.rate" label="BDT per unit" type="number" required fullWidth value={draft?.rate ?? ''}
                        inputProps={{ step: '0.0001', min: 0 }}
                        onChange={e => draft && setDraft({ ...draft, rate: e.target.value })} />
                    <TextField data-help="accounting.exchangeRates.form.field.source" label="Source" select fullWidth value={draft?.source ?? 'MANUAL'}
                        onChange={e => draft && setDraft({ ...draft, source: e.target.value as ExchangeRateSource })}>
                        {SOURCES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                    <TextField data-help="accounting.exchangeRates.form.field.remarks" label="Remarks" fullWidth value={draft?.remarks ?? ''} inputProps={{ maxLength: 500 }}
                        onChange={e => draft && setDraft({ ...draft, remarks: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.exchangeRates.form.cancel" onClick={() => setDraft(null)}>Cancel</Button>
                    <Button data-help="accounting.exchangeRates.form.save" variant="contained" disabled={busy || !draftValid} onClick={save}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog data-help-page="accounting.exchangeRates.import.page" open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Import exchange rates</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                    <Typography variant="body2" color="text.secondary">
                        Paste one rate per line as <code>date,currency,rate</code> (for example <code>2026-10-31,USD,122.50</code>).
                        An existing currency and date is updated. Lines with errors are skipped and listed below.
                    </Typography>
                    <TextField data-help="accounting.exchangeRates.import.field.csv" multiline minRows={8} fullWidth value={csv} placeholder="2026-10-31,USD,122.50"
                        inputProps={{ 'aria-label': 'Rates CSV' }} onChange={e => setCsv(e.target.value)} />
                    {importResult && (
                        <Alert severity={importResult.errors.length ? 'warning' : 'success'}>
                            {importResult.created} added, {importResult.updated} updated
                            {importResult.errors.length > 0 && (
                                <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                                    {importResult.errors.map(er => <li key={er}>{er}</li>)}
                                </ul>
                            )}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.exchangeRates.import.close" onClick={() => setImportOpen(false)}>Close</Button>
                    <Button data-help="accounting.exchangeRates.import.submit" variant="contained" disabled={busy || !csv.trim()} onClick={runImport}>Import</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ExchangeRates;
